import { createTheme, ThemeOptions } from '@mui/material/styles';

/* =============================================================================
 * HALO DESIGN SYSTEM TOKENS
 * Based on: https://www.figma.com/design/mhBj5EleHlao7UYls8Npo9/Halo-design-system
 * ===========================================================================*/

// Brand Colors
const COLORS = {
  // ML Red Scale
  mlRedDarker: '#610707',
  mlRedDark: '#610707',
  mlRed: '#a20b0b',
  mlRedLight: '#e55e5e',
  mlRedLighter: '#ee9595',
  mlRedLightest: '#f6c9c9',

  // ML Blue Scale
  mlBlueDarker: '#193856',
  mlBlueDark: '#193856',
  mlBlue: '#1c5386',
  mlBlueLight: '#1e5f9b',
  mlBlueLighter: '#8ba8c9',
  mlBlueLightest: '#dce3eb',

  // ML Grey Scale
  mlGreyDarker: '#5c5c5c',
  mlGreyDark: '#5c5c5c',
  mlGrey: '#9c9c9c',
  mlGreyLight: '#bcbcbc',
  mlGreyLighter: '#d0d0d0',
  mlGreyLightest: '#e4e4e4',
  mlGreyDivider: '#d9d9d9',

  // Black Scale
  black400: '#4e4e4e',
  black700: '#2f2f2f',
  black800: '#212121',
  black900: '#1b1b1b',

  // White Scale
  white100: '#fefefe',
  white200: '#f7f9fa',
  white300: '#eceef0',

  // Utilities
  danger: '#dc2626',
  success: '#16a34a',
  warning: '#f59e0b',
  disabled: '#8c8c8c',
};

/* Blog cover palette — editorial rotation for /blog cover cards.
 * Saturated but sophisticated; not constrained to mlRed/mlBlue. */
export const BLOG_COVER_COLORS = {
  blogPersimmon: '#e8743b',
  blogIndigo: '#324b8e',
  blogTeal: '#2f7a6c',
  blogClay: '#c66a47',
  blogPlum: '#7a3d5e',
} as const;

export type BlogCoverColorToken = keyof typeof BLOG_COVER_COLORS;

// Common theme settings
const commonTheme: ThemeOptions = {
  typography: {
    fontFamily: '"Inter", "Exo 2", sans-serif',
    h1: {
      fontFamily: '"Exo 2"',
      fontSize: '44px',
      fontWeight: 600,
      lineHeight: 1.75,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Exo 2"',
      fontSize: '30px',
      fontWeight: 600,
      lineHeight: 1.6,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Exo 2"',
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.75,
    },
    h4: {
      fontFamily: '"Exo 2"',
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h5: {
      fontFamily: '"Exo 2"',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: '"Exo 2"',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.43,
    },
    body1: {
      fontFamily: '"Inter"',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: '"Inter"',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.43,
    },
    subtitle1: {
      fontFamily: '"Inter"',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontFamily: '"Inter"',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.43,
    },
    caption: {
      fontFamily: '"Inter"',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1,
    },
    button: {
      fontFamily: '"Exo 2"',
      fontWeight: 500,
      textTransform: 'none',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 4,
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'light',
    primary: {
      main: COLORS.mlRed,
      light: COLORS.mlRedLight,
      dark: COLORS.mlRedDark,
      contrastText: COLORS.white100,
    },
    secondary: {
      main: COLORS.mlBlue,
      light: COLORS.mlBlueLight,
      dark: COLORS.mlBlueDark,
      contrastText: COLORS.white100,
    },
    error: {
      main: COLORS.danger,
      light: '#ef5350',
      dark: '#c62828',
      contrastText: COLORS.white100,
    },
    warning: {
      main: COLORS.warning,
      light: '#ffb74d',
      dark: '#f57f17',
      contrastText: COLORS.white100,
    },
    success: {
      main: COLORS.success,
      light: '#4caf50',
      dark: '#2e7d32',
      contrastText: COLORS.white100,
    },
    info: {
      main: COLORS.mlBlue,
      light: COLORS.mlBlueLight,
      dark: COLORS.mlBlueDark,
      contrastText: COLORS.white100,
    },
    background: {
      default: '#F5F8FC',
      paper: '#EDF3F8',
    },
    text: {
      primary: COLORS.black900,
      secondary: COLORS.mlGrey,
      disabled: COLORS.disabled,
    },
    divider: COLORS.mlGreyDivider,
    action: {
      hover: 'rgba(162, 11, 11, 0.04)',
      selected: 'rgba(162, 11, 11, 0.08)',
      disabled: COLORS.disabled,
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
    grey: {
      50: COLORS.white200,
      100: COLORS.white300,
      200: COLORS.mlGreyLightest,
      300: COLORS.mlGreyLighter,
      400: COLORS.mlGreyLight,
      500: COLORS.mlGrey,
      600: COLORS.mlGreyDark,
      700: COLORS.mlGreyDarker,
      800: COLORS.black400,
      900: COLORS.black900,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", sans-serif',
          backgroundColor: '#F5F8FC',
          transition: 'background-color 0.3s ease, color 0.3s ease',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '14px',
        },
        containedPrimary: {
          backgroundColor: COLORS.mlRed,
          '&:hover': {
            backgroundColor: COLORS.mlRedDark,
          },
        },
        containedSecondary: {
          backgroundColor: COLORS.mlBlue,
          '&:hover': {
            backgroundColor: COLORS.mlBlueDark,
          },
        },
        outlined: {
          borderColor: COLORS.mlGreyLight,
          color: COLORS.black900,
          '&:hover': {
            backgroundColor: COLORS.white300,
            borderColor: COLORS.mlGrey,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12)',
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: COLORS.mlRedLight,
      light: COLORS.mlRedLighter,
      dark: COLORS.mlRed,
      contrastText: COLORS.white100,
    },
    secondary: {
      main: COLORS.mlBlueLight,
      light: COLORS.mlBlueLighter,
      dark: COLORS.mlBlue,
      contrastText: COLORS.white100,
    },
    error: {
      main: COLORS.danger,
      light: '#ef5350',
      dark: '#c62828',
      contrastText: COLORS.white100,
    },
    warning: {
      main: COLORS.warning,
      light: '#ffb74d',
      dark: '#f57f17',
      contrastText: COLORS.white100,
    },
    success: {
      main: COLORS.success,
      light: '#4caf50',
      dark: '#2e7d32',
      contrastText: COLORS.white100,
    },
    info: {
      main: COLORS.mlBlueLight,
      light: COLORS.mlBlueLighter,
      dark: COLORS.mlBlue,
      contrastText: COLORS.white100,
    },
    background: {
      default: COLORS.black900,
      paper: COLORS.black800,
    },
    text: {
      primary: COLORS.white100,
      secondary: COLORS.mlGreyLighter,
      disabled: COLORS.disabled,
    },
    divider: COLORS.mlGreyDark,
    action: {
      hover: 'rgba(229, 94, 94, 0.08)',
      selected: 'rgba(229, 94, 94, 0.16)',
      disabled: COLORS.disabled,
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
    grey: {
      50: COLORS.white100,
      100: COLORS.white200,
      200: COLORS.white300,
      300: COLORS.mlGreyLightest,
      400: COLORS.mlGreyLight,
      500: COLORS.mlGrey,
      600: COLORS.mlGreyDark,
      700: COLORS.mlGreyDarker,
      800: COLORS.black700,
      900: COLORS.black900,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", sans-serif',
          backgroundColor: COLORS.black900,
          transition: 'background-color 0.3s ease, color 0.3s ease',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '14px',
        },
        containedPrimary: {
          backgroundColor: COLORS.mlRedLight,
          '&:hover': {
            backgroundColor: COLORS.mlRed,
          },
        },
        containedSecondary: {
          backgroundColor: COLORS.mlBlueLight,
          '&:hover': {
            backgroundColor: COLORS.mlBlue,
          },
        },
        outlined: {
          borderColor: COLORS.mlGreyDark,
          color: COLORS.white100,
          '&:hover': {
            backgroundColor: COLORS.black800,
            borderColor: COLORS.mlGrey,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.3)',
          borderRadius: 8,
          backgroundColor: COLORS.black800,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});
